import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { ToastrService } from '@app/services/toastr.service'
import { environment } from '@environments/environment'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { EventLandingService } from '../../services/event-landing.service'
import { LandingBreakpointsComponent } from './landing-breakpoints/landing-breakpoints.component'
import { LandingEmbedCodeComponent } from './landing-embed-code/landing-embed-code.component'
import { LandingMirrorLinksComponent } from './landing-mirror-links/landing-mirror-links.component'
import { LandingOptionsComponent } from './landing-options/landing-options.component'

@Component({
  selector: 'ngx-landing-side-nav',
  templateUrl: './landing-side-nav.component.html',
  styleUrls: ['./landing-side-nav.component.scss'],
  imports: [CommonModule, LandingMirrorLinksComponent, LandingEmbedCodeComponent, LandingOptionsComponent, LandingBreakpointsComponent]
})
export class LandingSideNavComponent implements OnInit, OnDestroy {
  @Input() event: ServerRepresentedEvent
  @Input() persona: Persona
  @Input() isLoading = true
  currentDescription = ''
  private _destroy$ = new Subject()

  constructor(private auth: AuthService, private landingService: EventLandingService, private toastr: ToastrService, private personaService: PersonaService) {}

  ngOnInit() {
    // Initialize currentDescription with the persona's public_description
    if (this.persona?.public_description) {
      this.currentDescription = this.persona.public_description
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  get formLink(): string {
    if (!this.event?.slug || !this.persona?.slug) return ''
    return `${environment.landingPageUrl}workspaces/${this.auth.currentWorkspace.slug}/events/${this.event?.slug}/forms/${this.persona?.slug}`
  }

  get rawFormLink(): string {
    return this.formLink.replace('/register/', '/public/')
  }

  get qrDownloadName(): string {
    return `${this.auth?.currentWorkspace?.slug}-${this.event?.slug}-${this.persona?.slug}`
  }

  /**
   * Publish landing page options and description
   */
  publishLandingPage(): void {
    if (!this.persona?.id) return

    // First update the description
    this.personaService
      .updateForm({
        id: this.persona.id,
        public_description: this.currentDescription || this.persona.public_description
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          // After description is updated successfully, publish landing page options
          this.landingService
            .publishLandingPage(this.persona.id)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
              next: () => {
                this.toastr.success('Success', 'Landing page published successfully')
              },
              error: () => {
                this.toastr.error('Error', 'Failed to publish landing page options')
              }
            })
        },
        error: () => {
          this.toastr.error('Error', 'Failed to update description')
        }
      })
  }
}
